import { Link } from 'react-router-dom';
import { Button, Card, Text, Title } from '@tremor/react';
import { t } from '../lib/i18n';
import type { User } from '../api/types';

interface Props {
  user: User;
  /** Optional override for the "you need role X" line; defaults to a
   *  generic "esta sección no está disponible para tu rol" if omitted.
   *  Pass when the route has a specific role gate worth naming
   *  (e.g. Archives = owner-only). */
  requiredRoleLabel?: string;
}

/**
 * Replaces the previous bare `<Card><Text>403</Text></Card>` pattern that
 * pages used when the user landed on a route their role doesn't allow.
 *
 * Two pieces:
 *  - A clear, role-aware reason ("no disponible para Contabilidad").
 *  - A list of pages they CAN reach, so they're not stranded. The list is
 *    computed from the same role-hide table the Header uses.
 *
 * Keeps the user inside the SPA — no browser-back required to recover.
 */
export default function AccessDenied({ user, requiredRoleLabel }: Props) {
  // Mirror Header.tsx's accountingHidden + owner-only logic so the "where
  // you CAN go" list reflects what's in the nav for this role.
  const accountingHidden = ['inventory', 'vendors', 'salespeople', 'lost'];
  const isAccountingHidden = (k: string) =>
    user.role === 'accounting' && accountingHidden.includes(k);
  const isOwner = user.role === 'owner';

  const links: { to: string; label: string; show: boolean }[] = [
    { to: '/',           label: t.nav.overview,    show: true },
    { to: '/sales',      label: t.nav.sales,       show: true },
    { to: '/parts',      label: t.nav.parts,       show: true },
    { to: '/inventory',  label: t.nav.inventory,   show: !isAccountingHidden('inventory') },
    { to: '/vendors',    label: t.nav.vendors,     show: !isAccountingHidden('vendors') },
    { to: '/vendedores', label: t.nav.salespeople, show: !isAccountingHidden('salespeople') },
    { to: '/ar',         label: t.nav.ar,          show: true },
    { to: '/lost',       label: t.nav.lost,        show: !isAccountingHidden('lost') },
    { to: '/reports',    label: t.nav.reports,     show: user.role !== 'staff' },
    { to: '/archives',   label: t.nav.archives,    show: isOwner },
  ].filter((l) => l.show);

  return (
    <Card className="max-w-2xl mx-auto">
      <Title>{t.access.title}</Title>
      <Text className="mt-2">
        {requiredRoleLabel
          ? t.access.requires.replace('{role}', requiredRoleLabel).replace('{you}', t.roles[user.role])
          : t.access.noAccess.replace('{you}', t.roles[user.role])}
      </Text>
      <Text className="mt-4 text-tremor-content text-sm">{t.access.availableFor}</Text>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {links.map((l) => (
          <Link
            key={l.to}
            to={l.to}
            className="text-xs px-2 py-1 rounded-tremor-small bg-tremor-background-muted hover:bg-tremor-brand-faint text-tremor-content-strong"
          >
            {l.label}
          </Link>
        ))}
      </div>
      <div className="mt-4">
        <Link to="/">
          <Button>{t.access.goHome}</Button>
        </Link>
      </div>
    </Card>
  );
}
